import { basePath } from "@Common/constants";
import type { AnalyticsPayloadSchema } from "@Common/types/bo/analytics_public/analytics_public";
import type { ComponentPropI } from "@Common/types/resources";
import { FormIntroWrapper } from "@Src/components/FormIntroWrapper";
import { letsgoPath } from "@Src/constants";
import { usePublicKey } from "@Src/contexts/PublicKeyContext";
import { sendEncryptedData } from "@Src/utils/encryption";
import { Box, Button, Paper, TextField as TextFieldMui } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useState } from "react";
import {
	Show,
	SimpleShowLayout,
	useDataProvider,
	useNotify,
	useRecordContext,
} from "react-admin";
import { useNavigate } from "react-router-dom";

const EmailForm = () => {
	const record = useRecordContext();
	const dataProvider = useDataProvider();
	const { publicKey } = usePublicKey();
	const notify = useNotify();
	const navigate = useNavigate();

	const [email, setEmail] = useState("");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		Sentry.setUser({
			email,
		});
		if (!record) return;

		// Fire-and-forget: send initial_start_before_submit event
		if (publicKey) {
			const beforePayload: AnalyticsPayloadSchema = {
				type: "initial_start_before_submit",
				email,
			};
			sendEncryptedData(
				dataProvider,
				"analytics_public",
				"-1",
				beforePayload,
				publicKey,
			).catch(() => {});
		}

		try {
			await dataProvider.create("applications_public", {
				data: { form_public_id: record.id, email },
			});

			// Fire-and-forget: send initial_start_after_submit event
			if (publicKey) {
				const afterPayload: AnalyticsPayloadSchema = {
					type: "initial_start_after_submit",
					email,
				};
				sendEncryptedData(
					dataProvider,
					"analytics_public",
					"-1",
					afterPayload,
					publicKey,
				).catch(() => {});
			}

			notify("✅ Email sent successfully", { type: "success" });
			setEmail("");
			navigate(`${basePath}${letsgoPath}`);
		} catch (_error: unknown) {
			notify("❌ Failed to send email", { type: "error" });
		}
	};

	return (
		<Box
			component="form"
			onSubmit={handleSubmit}
			sx={{
				height: "100vh",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				backgroundColor: "#fafafa",
				p: 2,
			}}
		>
			<Paper
				elevation={3}
				sx={{
					p: 4,
					minWidth: 350,
					maxWidth: 650,
					textAlign: "left",
					borderRadius: 3,
				}}
			>
				<TextFieldMui
					label="Please enter your email"
					type="email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					fullWidth
					required
					margin="normal"
				/>
				<div style={{ margin: "10px" }}></div>
				<Button type="submit" variant="contained" color="primary">
					Send Email
				</Button>
			</Paper>
		</Box>
	);
};

// Used for 1st step of forms answering
export const FormsPublicShow = (props: ComponentPropI) => {
	return (
		<Show disableAuthentication={props.isPublic}>
			<SimpleShowLayout>
				<FormIntroWrapper>
					<EmailForm />
				</FormIntroWrapper>
			</SimpleShowLayout>
		</Show>
	);
};
